import * as React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { InformationChip } from '@europrocurement/flexy-components';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { StatusStyleType } from '@europrocurement/l2d-modules/hooks/useStatusStyle';
import { DOSSIER_STATUS_AWAITING, DOSSIER_STATUS_TO_CHECK } from '../../../constants/status';

export type DossierStepStatusProps = {
    stepId: number;
    stepLabel: string;
    stepCompletion: number;
    statutId: number;
    statutLibelle: string;
    handleStyleStatus?: (statutId?: number | null | undefined) => StatusStyleType;
    source?: 'FRONT' | 'BACK';
};

const DossierStepStatus: React.FunctionComponent<DossierStepStatusProps> = function ({
    stepId,
    stepLabel,
    stepCompletion,
    statutId,
    statutLibelle,
    handleStyleStatus,
    source = 'FRONT',
}) {
    const { palette } = useTheme();
    const { getDossierStatusStyle } = useStatusStyle();

    const { sxStatus } =
        handleStyleStatus !== undefined
            ? handleStyleStatus(statutId)
            : getDossierStatusStyle(statutId);

    const resolvedLabel = React.useMemo(() => {
        if (source === 'FRONT') {
            if (statutId === DOSSIER_STATUS_TO_CHECK) return 'En cours de traitement';
            if (statutId === DOSSIER_STATUS_AWAITING) return 'En attente';
        }
        return statutLibelle;
    }, [statutLibelle, statutId, source]);

    const statusColor = (sxStatus as { color?: string })?.color ?? palette.primary.main;
    const displayValue = Math.max(stepCompletion, stepId > 0 ? 10 : 0);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
            }}
        >
            <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={20}
                    sx={{ color: palette.grey[200], position: 'absolute', top: 0, left: 0 }}
                />
                <CircularProgress
                    variant="determinate"
                    value={displayValue}
                    size={20}
                    sx={{ color: statusColor }}
                />
            </Box>
            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}
            >
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                    sx={{ fontSize: '11px' }}
                >
                    {stepLabel}
                </Typography>
                <InformationChip
                    color={(sxStatus as { color?: string })?.color ?? undefined}
                    tooltipTitle={resolvedLabel}
                    sx={{
                        ...sxStatus,
                        padding: '2px 4px',
                        height: '24px',
                        fontSize: '11px',
                        borderRadius: '6px',
                    }}
                    label={<Typography variant="l2dtableCell">{resolvedLabel}</Typography>}
                />
            </Box>
        </Box>
    );
};

export default DossierStepStatus;
